import { defineStore, type Store } from "pinia";
import joi from "joi";
import md5 from "md5";
import type {
  IDefaultState,
  IDefaultStoreActions,
  IDefultStoreGetters,
  IFileState,
} from "@/interfaces";
import type { StoreName, TabErrorSchemaTypes } from "@/types";
import helpers from "@/helpers";
import { globali18n } from "@/i18n";
import toImageList from "@/helpers/files/toImageList";
import {
  type DefaultResponse,
  type Maybe,
  type MeetingAgendaPointTemplate,
  type MeetingAgendaPointTemplateInput,
} from "@/graphql/generated-types";
import { useMeetingAgendaPoints } from "@/composables/useMeetingAgendaPoints";

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title"],
  pictures: ["imageList"],
};

export interface IMeetingAgendaPoint {
  id?: number;
  title: string;
  description: string;
  image?: string | null | undefined;
  imageList: IFileState[];
}

export interface IMeetingAgendaPointsState extends IDefaultState {
  item: IMeetingAgendaPoint;
  areCategoriesFetched: boolean;
  meetingAgendaPoints: ReturnType<typeof useMeetingAgendaPoints>;
}

export interface IMeetingAgendaPointsStoreActions extends IDefaultStoreActions {
  fetchCategories(): Promise<MeetingAgendaPointTemplate[]>;
  save(input: MeetingAgendaPointTemplateInput): Promise<Maybe<DefaultResponse>>;
  isCategoryNotValid(
    categories: MeetingAgendaPointTemplate[],
    id?: number,
  ): Promise<boolean>;
  deleteItem(): Promise<Maybe<DefaultResponse>>;
}

export type MeetingAgendaPointsStore = Store<
  string,
  IMeetingAgendaPointsState,
  IDefultStoreGetters<IMeetingAgendaPointsState>,
  IMeetingAgendaPointsStoreActions
>;

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IMeetingAgendaPointsState => ({
      tabErrors: {},
      originalHash: null,
      validation: null,
      areCategoriesFetched: false,
      meetingAgendaPoints: useMeetingAgendaPoints(true),
      item: {
        title: "",
        description: "",
        image: undefined,
        imageList: [],
      },
    }),
    actions: {
      currentHash(): string {
        const data = {
          ...this.getItem(),
          imageList: this.item.imageList,
        };
        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      getValidationSchema(
        categories: MeetingAgendaPointTemplate[],
        id: number = 0,
      ) {
        return joi.object({
          title: joi
            .string()
            .max(50)
            .required()
            .custom((value, helper) => {
              const exists = categories.some(
                (category) =>
                  category?.title.toLowerCase() === value.toLowerCase() &&
                  id.toString() !== category.id,
              );

              if (!exists) {
                return true;
              }

              const { t } = globali18n;
              return helper.error(t("global.error_category_exists"));
            }),
          imageList: joi.array().min(1).required(),
          description: joi.string().max(2000).optional(),
        });
      },
      async setItem(data: MeetingAgendaPointTemplate) {
        this.item = {
          id: data.id ? parseInt(data.id) : undefined,
          title: data.title,
          description: data.description || "",
          image: undefined,
          imageList: toImageList(data),
        };

        this.setOriginalHash();
        this.validation = null;
      },
      getItem() {
        const item = { ...this.item };

        return {
          id: item?.id?.toString(),
          title: item.title,
          image: item.image,
          description: item.description,
        };
      },
      async isCategoryNotValid(
        categories: MeetingAgendaPointTemplate[],
        id: number = 0,
      ) {
        const schema = this.getValidationSchema(categories, id);

        this.validation = helpers.forms.validate(schema, {
          title: this.item.title,
          imageList: this.item.imageList.filter(
            (item) => item.action !== "delete",
          ),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async save(input: MeetingAgendaPointTemplateInput) {
        return await this.meetingAgendaPoints.save(input);
      },
      async deleteItem() {
        if (!this.item.id) {
          return;
        }

        return await this.meetingAgendaPoints.deleteItem(this.item.id);
      },
    },
  });
};

export const useMeetingsAgendaPointsStore = createUseStore(
  "MEETINGS_AGENDA_POINTS",
);
export const useAddMeetingsAgendaPointsStore = createUseStore(
  "MEETINGS_ADD_AGENDA_POINTS",
);
